import { createSlice } from '@reduxjs/toolkit';
import {
  fetchEmployeeLeaves,
  fetchPendingLeaves,
  fetchAllLeaves,
  applyLeave,
  deptHeadApproval,
  hrApproval,
  fetchLeaveBalance,
  fetchLeaveSummary,
} from './leaveThunks';

const initialState = {
  // ── Employee's own leaves ─────────────────────────────────────────────────
  employeeLeaves:        [],
  employeeLeavesLoading: false,
  employeeLeavesError:   null,

  // ── Leaves pending the current user's approval ───────────────────────────
  pendingLeaves:        [],
  pendingLeavesLoading: false,
  pendingLeavesError:   null,
  // Which approval level the current user can act on:
  //   "ADMIN" | "DEPARTMENT_HEAD" | "HR" | null
  approvalLevel: null,
  pendingCount:  0,    // backend count from /pending/ — used for badge

  // ── All leaves (HR / admin list view) ─────────────────────────────────────
  allLeaves:        [],
  allLeavesLoading: false,
  allLeavesError:   null,

  // ── Leave Balance ─────────────────────────────────────────────────────────
  leaveBalance:   { casual: 0, medical: 0, earned: 0, total: 0 },
  balanceLoading: false,
  balanceError:   null,

  // ── Leave Summary ─────────────────────────────────────────────────────────
  leaveSummary:   { totalLeaves: 0, pending: 0, approved: 0, rejected: 0 },
  summaryLoading: false,
  summaryError:   null,

  // ── Filters (HR list view) ────────────────────────────────────────────────
  leaveFilters: {
    status:    'all', // 'all' | backend status string
    employee:  '',
    fromDate:  '',
    toDate:    '',
    leaveType: '',
  },

  // ── Apply leave ───────────────────────────────────────────────────────────
  isApplying:   false,
  applyError:   null,
  applySuccess: false,

  // ── Approval action (dept head OR HR) ─────────────────────────────────────
  isUpdating:   false,
  updateError:  null,
  updateSuccess: false,
  updateMessage: null,   // backend message after approve/reject
};

// ── Helper: patch a leave inside any array by id ─────────────────────────────
const patchLeaveInArray = (arr, leaveId, patches) => {
  const idx = arr.findIndex((l) => l.id === leaveId);
  if (idx !== -1) Object.assign(arr[idx], patches);
};

// ── Helper: derive frontend fields from the backend approval response ────────
//   leave: { id, status, dept_head_approved, hr_approved?, fully_approved? }
//   Backend approval endpoints always return raw status (PENDING_HR, APPROVED, etc.)
const deriveApprovalPatches = (backendLeave) => {
  const raw = backendLeave.status || '';  // always raw from approval endpoints

  const friendlyStatus =
    raw === 'PENDING_DEPT_HEAD' || raw === 'PENDING_HR' ? 'PENDING'  :
    raw === 'APPROVED'                                   ? 'APPROVED' :
    raw === 'REJECTED_BY_DEPT_HEAD' || raw === 'REJECTED_BY_HR' ? 'REJECTED' :
    raw;

  return {
    status:            friendlyStatus,
    rawStatus:         raw,
    displayStatus:     statusLabel(raw),
    deptHeadApproved:  !!backendLeave.dept_head_approved,
    hrApproved:        !!backendLeave.hr_approved,
    isPending:         friendlyStatus === 'PENDING',
    isApproved:        friendlyStatus === 'APPROVED',
    isRejected:        friendlyStatus === 'REJECTED',
    requiresDeptHead:  raw === 'PENDING_DEPT_HEAD',
    requiresHR:        raw === 'PENDING_HR',
    rejectedByDeptHead: raw === 'REJECTED_BY_DEPT_HEAD',
    rejectedByHR:       raw === 'REJECTED_BY_HR',
  };
};

const statusLabel = (s) => {
  const m = {
    PENDING_DEPT_HEAD:     'Pending Dept Head',
    PENDING_HR:            'Pending HR',
    APPROVED:              'Approved',
    REJECTED_BY_DEPT_HEAD: 'Rejected by Dept Head',
    REJECTED_BY_HR:        'Rejected by HR',
  };
  return m[s] || s || 'Unknown';
};

const leaveSlice = createSlice({
  name: 'leave',
  initialState,
  reducers: {
    setLeaveFilters: (state, action) => {
      state.leaveFilters = { ...state.leaveFilters, ...action.payload };
    },
    clearLeaveFilters: (state) => {
      state.leaveFilters = initialState.leaveFilters;
    },
    clearApplySuccess: (state) => {
      state.applySuccess = false;
      state.applyError   = null;
    },
    clearUpdateSuccess: (state) => {
      state.updateSuccess = false;
      state.updateError   = null;
      state.updateMessage = null;
    },
  },

  extraReducers: (builder) => {
    // ================================================================
    // FETCH EMPLOYEE LEAVES
    // ================================================================
    builder
      .addCase(fetchEmployeeLeaves.pending, (state) => {
        state.employeeLeavesLoading = true;
        state.employeeLeavesError   = null;
      })
      .addCase(fetchEmployeeLeaves.fulfilled, (state, action) => {
        state.employeeLeavesLoading = false;
        state.employeeLeaves        = action.payload;
      })
      .addCase(fetchEmployeeLeaves.rejected, (state, action) => {
        state.employeeLeavesLoading = false;
        state.employeeLeavesError   = action.payload || 'Failed to fetch leaves';
      });

    // ================================================================
    // FETCH PENDING LEAVES
    //   payload: { leaves: [...], approvalLevel: "ADMIN"|"DEPARTMENT_HEAD"|"HR"|null }
    // ================================================================
    builder
      .addCase(fetchPendingLeaves.pending, (state) => {
        state.pendingLeavesLoading = true;
        state.pendingLeavesError   = null;
      })
      .addCase(fetchPendingLeaves.fulfilled, (state, action) => {
        state.pendingLeavesLoading = false;
        state.pendingLeaves        = action.payload.leaves;
        state.approvalLevel        = action.payload.approvalLevel;
        state.pendingCount         = action.payload.count;
      })
      .addCase(fetchPendingLeaves.rejected, (state, action) => {
        state.pendingLeavesLoading = false;
        state.pendingLeavesError   = action.payload || 'Failed to fetch pending leaves';
      });

    // ================================================================
    // FETCH ALL LEAVES (HR list)
    // ================================================================
    builder
      .addCase(fetchAllLeaves.pending, (state) => {
        state.allLeavesLoading = true;
        state.allLeavesError   = null;
      })
      .addCase(fetchAllLeaves.fulfilled, (state, action) => {
        state.allLeavesLoading = false;
        state.allLeaves        = action.payload;
      })
      .addCase(fetchAllLeaves.rejected, (state, action) => {
        state.allLeavesLoading = false;
        state.allLeavesError   = action.payload || 'Failed to fetch all leaves';
      });

    // ================================================================
    // APPLY LEAVE
    // ================================================================
    builder
      .addCase(applyLeave.pending, (state) => {
        state.isApplying   = true;
        state.applyError   = null;
        state.applySuccess = false;
      })
      .addCase(applyLeave.fulfilled, (state, action) => {
        state.isApplying   = false;
        state.applySuccess = true;
        // Prepend the new leave to the employee list immediately
        state.employeeLeaves.unshift(action.payload);
      })
      .addCase(applyLeave.rejected, (state, action) => {
        state.isApplying = false;
        state.applyError = action.payload || 'Failed to apply for leave';
      });

    // ================================================================
    // DEPT HEAD APPROVAL (first level)
    //   payload: { leaveId, action, message, leave: { id, status, dept_head_approved, requires_hr_approval } }
    // ================================================================
    builder
      .addCase(deptHeadApproval.pending, (state) => {
        state.isUpdating   = true;
        state.updateError  = null;
        state.updateSuccess = false;
      })
      .addCase(deptHeadApproval.fulfilled, (state, action) => {
        state.isUpdating    = false;
        state.updateSuccess = true;
        state.updateMessage = action.payload.message;

        const patches = deriveApprovalPatches(action.payload.leave);
        const leaveId = action.payload.leaveId;

        // Patch in all three lists
        patchLeaveInArray(state.employeeLeaves, leaveId, patches);
        patchLeaveInArray(state.pendingLeaves,  leaveId, patches);
        patchLeaveInArray(state.allLeaves,      leaveId, patches);
      })
      .addCase(deptHeadApproval.rejected, (state, action) => {
        state.isUpdating  = false;
        state.updateError = action.payload || 'Failed to process department head approval';
      });

    // ================================================================
    // HR APPROVAL (second level)
    //   payload: { leaveId, action, message, leave: { id, status, dept_head_approved, hr_approved, fully_approved } }
    // ================================================================
    builder
      .addCase(hrApproval.pending, (state) => {
        state.isUpdating   = true;
        state.updateError  = null;
        state.updateSuccess = false;
      })
      .addCase(hrApproval.fulfilled, (state, action) => {
        state.isUpdating    = false;
        state.updateSuccess = true;
        state.updateMessage = action.payload.message;

        const patches = deriveApprovalPatches(action.payload.leave);
        const leaveId = action.payload.leaveId;

        patchLeaveInArray(state.employeeLeaves, leaveId, patches);
        patchLeaveInArray(state.pendingLeaves,  leaveId, patches);
        patchLeaveInArray(state.allLeaves,      leaveId, patches);
      })
      .addCase(hrApproval.rejected, (state, action) => {
        state.isUpdating  = false;
        state.updateError = action.payload || 'Failed to process HR approval';
      });

    // ================================================================
    // FETCH LEAVE BALANCE
    // ================================================================
    builder
      .addCase(fetchLeaveBalance.pending, (state) => {
        state.balanceLoading = true;
        state.balanceError   = null;
      })
      .addCase(fetchLeaveBalance.fulfilled, (state, action) => {
        state.balanceLoading = false;
        state.leaveBalance   = action.payload;
      })
      .addCase(fetchLeaveBalance.rejected, (state, action) => {
        state.balanceLoading = false;
        state.balanceError   = action.payload || 'Failed to fetch leave balance';
      });

    // ================================================================
    // FETCH LEAVE SUMMARY
    // ================================================================
    builder
      .addCase(fetchLeaveSummary.pending, (state) => {
        state.summaryLoading = true;
        state.summaryError   = null;
      })
      .addCase(fetchLeaveSummary.fulfilled, (state, action) => {
        state.summaryLoading = false;
        state.leaveSummary   = action.payload;
      })
      .addCase(fetchLeaveSummary.rejected, (state, action) => {
        state.summaryLoading = false;
        state.summaryError   = action.payload || 'Failed to fetch leave summary';
      });
  },
});

export const {
  setLeaveFilters,
  clearLeaveFilters,
  clearApplySuccess,
  clearUpdateSuccess,
} = leaveSlice.actions;

export default leaveSlice.reducer;